/**
 * The side panel (slice 1 spec §7.1) is URL state on any shell page:
 * `?panel=open` (F7: a new thread), `?panel=<threadId>` (F9: that thread), and
 * no `panel` (F8: the collapsed rail). Every change pushes a history entry, so
 * Back and Forward walk the panel states. A thread rail sits beside the thread
 * (a strip under the header on a phone), so every recent thread and a new one
 * are a click away, and opening the panel goes back to the last thread shown.
 */
import {
  CollapsedRail,
  RailMoreButton,
  SidePanel,
  SidePanelHeader,
  ThreadFinder,
  ThreadFinderItem,
  ThreadRail,
  ThreadStrip,
  type RailThread,
} from "@crew/ui/components/crew-side-panel";
import { buttonVariants } from "@crew/ui/components/button";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { agentTint } from "../../app/tints";
import { useThread, useThreads, type ThreadDetail, type ThreadItem } from "./api";
import { shortTime } from "./format";
import {
  forgetThread,
  panelHref,
  rememberThread,
  resumePanel,
  usePanel,
  withPanel,
} from "./panel-state";
import { detailItem, railThreads, threadTitle, tileLetter, tileStatus, tileTint } from "./rail";
import { StartComposer } from "./start-composer";
import { startDrafts } from "./starts";
import { emptyNote, findThreads, loadedThreads } from "./switcher";
import { ThreadView } from "./thread-view";
import { ThreadTasksChip } from "../tasks/parts";
import { MessagingMark } from "../messaging/parts";
import { messagingHeader } from "../messaging/view";
import { useAttach } from "../docs/attach";

type SetPanel = ReturnType<typeof usePanel>[1];

/** A thread's dot and trailing words: what runs now, else when it last moved. */
function threadStatus(thread: ThreadItem, me: Me) {
  if (thread.activity === "running")
    return { dot: "text-moss", meta: "Working", tone: "text-moss" };
  if (thread.activity === "queued") return { dot: "text-hint", meta: "Queued", tone: undefined };
  // Slice 6c §7.1: a handoff in it waits on someone.
  if (thread.activity === "handoff")
    return { dot: "text-copper", meta: "Waiting on you", tone: "text-copper" };
  return {
    dot: undefined,
    meta: shortTime(thread.last_message_at, me.workspace.timezone),
    tone: undefined,
  };
}

function railThread(thread: ThreadItem, me: Me, href: string): RailThread {
  const status = threadStatus(thread, me);
  return {
    id: thread.id,
    title: thread.title,
    href,
    letter: tileLetter(thread.title),
    tint: tileTint(thread.id),
    icon: thread.icon ?? null,
    imessage: thread.messaging_app === "imessage",
    status: tileStatus(thread.activity),
    meta: status.meta,
    metaClassName: status.tone,
    agent: tileAgent(thread),
  };
}

/** The thread's agent, for its tile's corner and its labels. */
function tileAgent(thread: ThreadItem) {
  const { agent } = thread;
  return agent && { name: agent.name, glyph: agent.glyph, tint: agentTint(agent.tint) };
}

/** The rail's tiles and "+N" count, from the loaded Mine threads and the open one. */
function useRail(me: Me, open?: ThreadItem) {
  const threads = useThreads("mine");
  const { search } = useLocation();
  const { shown, more } = railThreads(loadedThreads(threads.data), open);
  return {
    threads: shown.map((t) => railThread(t, me, panelHref(search, t.id))),
    more,
    newHref: panelHref(search, "open"),
  };
}

/** "+N": every loaded thread in a finder, with a search box and Open Chats. */
function AllThreads({
  me,
  more,
  current,
  side,
}: {
  me: Me;
  more: number;
  current?: string;
  side: "right" | "left" | "bottom";
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const close = () => {
    setOpen(false);
    setQuery("");
  };
  return (
    <ThreadFinder
      trigger={<RailMoreButton count={more} />}
      side={side}
      open={open}
      onOpenChange={(next) => (next ? setOpen(true) : close())}
      query={query}
      onQueryChange={setQuery}
      footer={
        <Link href="/chats" onClick={close} className="hover:text-foreground">
          Open Chats
        </Link>
      }
    >
      <FinderRows me={me} query={query} current={current} onPick={close} />
    </ThreadFinder>
  );
}

/** The Mine threads that match `query`; picking one closes the finder. */
function FinderRows({
  me,
  query,
  current,
  onPick,
}: {
  me: Me;
  query: string;
  current?: string;
  onPick: () => void;
}) {
  const threads = useThreads("mine");
  const { search } = useLocation();
  const loaded = loadedThreads(threads.data);
  const shown = findThreads(loaded, query);
  return (
    <>
      <FinderNote
        error={threads.error}
        loading={threads.isPending}
        note={emptyNote(loaded.length, shown.length)}
      />
      {shown.map((thread) => {
        const status = threadStatus(thread, me);
        return (
          <ThreadFinderItem
            key={thread.id}
            href={panelHref(search, thread.id)}
            current={thread.id === current}
            dot={status.dot}
            meta={status.meta}
            metaClassName={status.tone}
            agent={tileAgent(thread)}
            onClick={onPick}
          >
            {thread.title}
          </ThreadFinderItem>
        );
      })}
    </>
  );
}

/** In place of rows: the load's error, a spinner, or why there are none. */
function FinderNote({
  error,
  loading,
  note,
}: {
  error: Error | null;
  loading: boolean;
  note: string | null;
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-4 text-muted-foreground">
        <Spinner className="size-4" />
      </div>
    );
  }
  const text = error ? errorHint(error) : note;
  return text ? <p className="px-2.5 py-2 text-meta text-muted-foreground">{text}</p> : null;
}

/**
 * The open panel: the rail (desktop) beside a column with the header, the strip
 * (phone) and the body.
 */
function PanelFrame({
  me,
  current,
  open,
  header,
  onClose,
  children,
}: {
  me: Me;
  /** The open thread's id, or "new". */
  current: string;
  /** The open thread, kept on the rail even when it isn't among the loaded ones. */
  open?: ThreadItem;
  header: ReactNode;
  /** Collapse, also a swipe right on a phone. */
  onClose: () => void;
  children: ReactNode;
}) {
  const rail = useRail(me, open);
  return (
    <SidePanel onSwipeClose={onClose}>
      <ThreadRail
        threads={rail.threads}
        current={current}
        newHref={rail.newHref}
        more={<AllThreads me={me} more={rail.more} current={current} side="right" />}
      />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {header}
        <ThreadStrip
          threads={rail.threads}
          current={current}
          newHref={rail.newHref}
          more={<AllThreads me={me} more={rail.more} current={current} side="bottom" />}
        />
        {children}
      </div>
    </SidePanel>
  );
}

/** F7: a new thread for the Chief of Staff, the composer at the bottom. */
function PanelNew({ me, setPanel }: { me: Me; setPanel: SetPanel }) {
  const navigate = useNavigate();
  const attach = useAttach();
  const first = me.user.name.split(/\s+/)[0];
  const close = () => setPanel(null);
  return (
    <PanelFrame
      me={me}
      current="new"
      onClose={close}
      header={
        <SidePanelHeader
          title="New thread"
          subtitle="Goes to the Chief of Staff"
          onExpand={() => navigate("/chats")}
          onCollapse={close}
        />
      }
    >
      {/* `mt-auto`, not `justify-end`: on a short screen it scrolls to its top. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pt-6 pb-5">
        <div className="mt-auto flex flex-col gap-2">
          <h2 className="font-display text-title text-foreground">What's next, {first}?</h2>
          <p className="text-sm text-muted-foreground">
            Say what done looks like. The Chief of Staff picks it up, or hands it to someone on the
            crew.
          </p>
        </div>
      </div>
      <div className="shrink-0 border-t border-border px-4 pt-3 pb-4">
        <StartComposer
          placeholder="Ask for something. Say what done looks like."
          label="Start a thread"
          attach={attach}
          surface="panel"
          onStarted={setPanel}
        />
      </div>
    </PanelFrame>
  );
}

/** Under a live thread's title: what it's doing. */
const LIVE_SUBTITLE: Record<
  NonNullable<ThreadItem["activity"]>,
  { text: string; tone?: string }
> = {
  running: { text: "Working", tone: "text-moss" },
  queued: { text: "Queued", tone: undefined },
  // Slice 6c §7.1: a handoff in it waits on someone.
  handoff: { text: "Waiting on you", tone: "text-copper" },
};

/** Who the thread is with, named: the Chief of Staff until the list says otherwise. */
const agentName = (thread?: ThreadItem) => thread?.agent?.name ?? "Chief of Staff";

/** Under the thread's title: what it's doing, else who it's with. */
function threadSubtitle(thread?: ThreadItem): { text: string; tone?: string } {
  return (thread?.activity && LIVE_SUBTITLE[thread.activity]) || { text: agentName(thread) };
}

/**
 * Keep the open thread as "where you left off". Once it no longer loads, forget it and
 * show a new thread in its place.
 */
function useRememberThread(
  workspaceId: string,
  threadId: string,
  error: Error | null,
  setPanel: SetPanel,
) {
  // Kept as soon as it opens; the thread kept before it goes back if this one is gone.
  // Recorded once per thread opened: a repeated effect (React's dev double run) would
  // otherwise see this thread already kept and lose the one before it.
  const before = useRef<{ threadId: string; kept: string | null } | null>(null);
  const gone = error instanceof ApiError && error.status === 404;
  useEffect(() => {
    const kept = rememberThread(workspaceId, threadId);
    if (before.current?.threadId !== threadId) before.current = { threadId, kept };
  }, [workspaceId, threadId]);
  useEffect(() => {
    if (!gone) return;
    forgetThread(workspaceId, threadId, before.current?.kept ?? null);
    setPanel("open", { replace: true });
  }, [gone, workspaceId, threadId, setPanel]);
}

/** An item for the rail only when it's one of mine (the rail holds Mine threads). */
const mineOnly = (item: ThreadItem | undefined, userId: string) =>
  item?.started_by_user_id === userId ? item : undefined;

/** The open thread's row: from the list, else (an older thread) worked out from its detail. */
const knownThread = (
  detail: ThreadDetail | undefined,
  listed: ThreadItem | undefined,
  agents: Me["agents"],
) => listed ?? (detail && detailItem(detail, agents));

/** The open thread's header and rail item. */
function threadHeader(detail: ThreadDetail | undefined, listed: ThreadItem | undefined, me: Me) {
  const known = knownThread(detail, listed, me.agents);
  return {
    title: threadTitle(detail, known),
    subtitle: threadSubtitle(known),
    open: mineOnly(known, me.user.id),
  };
}

/** F9: one thread, the same `ThreadView` Chats renders. */
function PanelThread({ me, threadId, setPanel }: { me: Me; threadId: string; setPanel: SetPanel }) {
  const navigate = useNavigate();
  const [, setParams] = useSearchParams();
  const thread = useThread(threadId);
  const listed = loadedThreads(useThreads("mine").data).find((t) => t.id === threadId);
  const { title, subtitle, open } = threadHeader(thread.data, listed, me);
  // Slice 2e (spec §6.3; Paper 64): an iMessage thread's mark and line.
  const texting = thread.data ? messagingHeader(thread.data, me) : null;
  useRememberThread(me.workspace.id, threadId, thread.error, setPanel);
  const close = () => setPanel(null);
  return (
    <PanelFrame
      me={me}
      current={threadId}
      open={open}
      onClose={close}
      header={
        <SidePanelHeader
          title={title}
          mark={
            texting ? (
              <MessagingMark app={thread.data?.thread.messaging_app} size={14} />
            ) : undefined
          }
          subtitle={texting?.line ?? subtitle.text}
          // One line on a phone, as Chats' thread header.
          subtitleClassName={cn("hidden md:block", texting ? undefined : subtitle.tone)}
          actions={
            <>
              {texting?.linkAgain ? (
                <Link
                  href="/settings/messaging"
                  className={buttonVariants({ variant: "outline", size: "xs" })}
                >
                  Link again
                </Link>
              ) : null}
              <ThreadTasksChip tasks={thread.data?.tasks} />
            </>
          }
          onExpand={() => navigate(`/chats/${threadId}`)}
          onCollapse={close}
        />
      }
    >
      <ThreadView
        key={threadId}
        threadId={threadId}
        me={me}
        size="sm"
        onStartEdited={({ body, doc_id, files }) => {
          // The thread never existed: New thread in its place, with its words, files and doc chip.
          forgetThread(me.workspace.id, threadId);
          startDrafts.put("panel", body, null, files);
          setParams(
            (prev) => {
              const next = withPanel(prev, "open");
              if (doc_id) next.set("attach", doc_id);
              return next;
            },
            { replace: true },
          );
        }}
      />
    </PanelFrame>
  );
}

/** F8: the collapsed panel, the rail on its own at the page's edge. */
function PanelCollapsed({ me, setPanel }: { me: Me; setPanel: SetPanel }) {
  const rail = useRail(me);
  return (
    <CollapsedRail
      threads={rail.threads}
      newHref={rail.newHref}
      waiting={me.counts.open_approvals_mine}
      onExpand={() => setPanel(resumePanel(me.workspace.id))}
      more={<AllThreads me={me} more={rail.more} side="left" />}
    />
  );
}

export function CrewPanel({ me }: { me: Me }) {
  const [panel, setPanel] = usePanel();
  if (panel === "open") return <PanelNew me={me} setPanel={setPanel} />;
  if (panel) return <PanelThread me={me} threadId={panel} setPanel={setPanel} />;
  return <PanelCollapsed me={me} setPanel={setPanel} />;
}
